import type { RomanApiError } from "@roman/shared/lib/net/romanApi";
import type { Language } from "@roman/shared/types/Language";
import { useMemo } from "react";
import { useRoundEntryUnit } from "./useRoundEntryUnit";
import { useScheduledUserEntries } from "./useScheduledUserEntries";
import { useSoccerRound } from "./useSoccerRound";

export const useSchedule = (
  roundEntryUnitId: string,
  userId: string | undefined,
  handleError: (err: RomanApiError) => void,
  lang: Language = "en",
  dedupingInterval?: number,
) => {
  // Round & RoundEntryUnit information is not updated frequently, so we can use `pollInterval` to avoid unnecessary requests.
  const { roundEntryUnit, isLoading: roundEntryUnitLoading } = useRoundEntryUnit({
    roundEntryUnitId,
    lang,
    swrOptions: {
      dedupingInterval: dedupingInterval,
      onError: handleError,
    },
  });

  const { data: roundData, isLoading: roundLoading } = useSoccerRound({
    roundId: roundEntryUnit?.roundId || null,
    lang,
    swrOptions: {
      dedupingInterval: dedupingInterval,
      onError: handleError,
    },
  });

  const { data: roundUnitScheduleData, isLoading: scheduleLoading } = useScheduledUserEntries(
    userId,
    roundEntryUnitId,
    handleError,
  );

  const isLoading = roundEntryUnitLoading || roundLoading || scheduleLoading;

  const totalEntries = useMemo(() => {
    return roundUnitScheduleData?.entries.length || 0;
  }, [roundUnitScheduleData?.entries]);

  return {
    roundEntryUnit,
    round: roundData?.round,
    entries: roundUnitScheduleData?.entries || [],
    totalEntries: totalEntries,
    isLoading,
  };
};
